<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="fields"
      :table-filter="{ external: true, placeholder: $t('table.user.placeholderSearch') || 'ค้นหาเลขที่ใบงาน, รายละเอียด...', label: 'ค้นหา:' }"
      :table-filter-value="searchKeyword"
      @update:table-filter-value="onSearchChange"
      hover
      :pagination="false"
      :loading="isLoading"
      :items-per-page-select="{
        label: $t('table.common.itemsPerPage') || 'แสดงต่อหน้า:',
        values: [10, 20, 50],
        external: true
      }"
      :items-per-page="pageSize"
      @pagination-change="onPageSizeChange"
    >
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #reported_datetime="{ item }">
        <td>
          <div>{{ formatDate(item.userPart.dateTimeInformed) }}</div>
        </td>
      </template>

      <template #username="{ item }">
        <td>
          <div class="font-weight-bold">{{ item.username }}</div>
          <div v-if="item.onBehalfOf" class="small text-danger">
            (แจ้งแทน: {{ item.onBehalfOf }})
          </div>
          <div class="small text-muted">{{ item.userEmail }}</div>
          <span v-if="item._role === 'reporter'" class="role-badge badge-reporter">แจ้งซ่อม</span>
          <span v-else class="role-badge badge-dept">งานแผนก</span>
        </td>
      </template>

      <template #issue_details="{ item }">
        <td>
          <div>
            <span class="text-primary font-weight-bold">{{ getCategoryName(item) }}</span>
            <br />
            <span class="text-muted">{{ getSubCategoryName(item) }}</span>
          </div>
        </td>
      </template>

      <template #rating="{ item }">
        <td class="text-center">
          <div style="white-space: nowrap;">
            <span
              v-for="n in 5"
              :key="n"
              style="font-size: 18px; margin: 0 1px;"
              :style="{ color: n <= getRatingStars(item) ? '#FFD700' : '#E4E5E9' }"
            >
              ★
            </span>
          </div>
        </td>
      </template>

      <template #actions="{ item }">
        <td class="text-center">
          <CButton color="info" variant="outline" size="sm" class="circle-btn" @click="onRowClick(item)"
            title="ดูรายละเอียด">
            <CIcon name="cil-magnifying-glass" size="sm" />
          </CButton>
        </td>
      </template>
    </CDataTable>

    <CPagination
      v-show="totalPages > 1"
      :pages="totalPages"
      :active-page.sync="currentPage"
      :double-arrows="false"
      align="center"
    />

  </CCardBody>
</template>

<style>
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}
.custom-pagination .page-item .page-link {
  color: #414141;
}
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}
.circle-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 3px;
}
.badge-reporter {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}
.badge-dept {
  background-color: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #ce93d8;
}
</style>

<script>
import { mapGetters } from "vuex";

const fields = [
  { key: "ticketNumber", label: "เลขที่ใบแจ้งซ่อม", _style: "width:15%;" },
  { key: "reported_datetime", label: "วัน/เวลา แจ้ง", _style: "width:15%;" },
  { key: "username", label: "ผู้แจ้ง", _style: "width:20%;" },
  { key: "issue_details", label: "รายละเอียด", _style: "width:30%;" },
  {
    key: "rating",
    label: "ความพึงพอใจ",
    _style: "width:15%; text-align:center;",
    class: "text-center"
  },
  { key: "actions", label: "", _style: "width:5%;", sorter: false, filter: false },
];

export default {
  name: "Shistorytable",
  props: {
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const saved = Number(localStorage.getItem('smartfix_page_size'));
    const initialPageSize = [10, 20, 50].includes(saved) ? saved : 50;
    return {
      ticketsData: [],
      fields,
      isLoading: false,
      currentPage: 1,
      pageSize: initialPageSize,
      totalItems: 0,
      searchKeyword: "",
      searchTimeout: null,
      isMounted: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize) || 1;
    },
  },
  watch: {
    currentPage() {
      if (this.isMounted && !this.lazy) this.loadHistoryTickets();
    },
    profile(val) {
      if (val && !this.lazy && this.isMounted) this.loadHistoryTickets();
    }
  },
  mounted() {
    this.isMounted = true;
    if (this.profile && !this.lazy) {
      this.loadHistoryTickets();
    }
  },
  methods: {
    onPageSizeChange(newSize) {
      const size = Number(newSize);
      if ([10, 20, 50].includes(size) && size !== this.pageSize) {
        this.pageSize = size;
        localStorage.setItem('smartfix_page_size', String(size));
        this.currentPage = 1;
        this.loadHistoryTickets();
      }
    },

    onSearchChange(val) {
      this.searchKeyword = val || "";
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadHistoryTickets();
      }, 350);
    },

    async loadHistoryTickets() {
      try {
        if (!this.profile) return;

        let departmentId = null;
        if (this.profile.category) {
             departmentId = this.profile.category._id || this.profile.category;
        } else if (this.profile.department) {
             departmentId = this.profile.department._id || this.profile.department;
        }

        if (!departmentId) {
            console.error("ไม่พบข้อมูลแผนกของ Supervisor");
            return;
        }

        this.isLoading = true;
        const supervisorId = this.profile._id;

        const params = new URLSearchParams({
          page: this.currentPage,
          limit: this.pageSize,
        });
        if (supervisorId) {
          params.append('supervisorId', supervisorId);
        }
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.append('search', this.searchKeyword.trim());
        }

        const res = await fetch(`${process.env.VUE_APP_APP_URL}/ticket/supervisor/${departmentId}/history?${params.toString()}`)
          .then(r => r.json());

        if (res.success && Array.isArray(res.data)) {
          this.ticketsData = res.data.map(ticket => {
            const user = ticket.userPart?.user || {};
            const isReporter = String(user._id || ticket.userPart?.user) === String(supervisorId);
            return {
              ...ticket,
              _role: isReporter ? 'reporter' : 'dept',
              username: `${user.name || ''} ${user.surname || ''}`.trim() || 'ไม่ระบุตัวตน',
              onBehalfOf: ticket.userPart?.onBehalfOf || '',
              userEmail: user.email || '-',
            };
          });
          this.totalItems = res.pagination?.total ?? this.ticketsData.length;
        }
      } catch (error) {
        console.error("Failed to load tickets:", error);
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      if(!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes} น.`;
    },

    getCategoryName(item) {
      return item.userPart?.category?.departmentName || "ไม่ระบุ";
    },

    getSubCategoryName(item) {
      const sub = item.userPart?.subCategory;
      if (!sub) return "-";
      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }
      return sub;
    },

    getRatingStars(item) {
      return item.rating?.stars || 0;
    },

    onRowClick(item) {
      this.$emit("show-detail", item);
    },
  },
};
</script>
